import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { validateProps } from '../../../../../utils/propValidator';
import { ModalWrapper } from '../../feedback';
import { SectionTitle } from '../../layout';
import {
  generateUploaderErrors,
  generateUploaderHints,
  getAspectRatio,
  resolveMinImages,
  validateFileToUpload,
} from './imageUpload.utils';
import { MultiImageGrid, SingleImagePreview } from './ImageUploaderGrid';

const propRules = {
  open: { type: 'boolean', required: true },
  onClose: { type: 'function', required: true },
  multiple: { type: 'boolean' },
  initialValue: { type: 'array' },
  onSave: { type: 'function' },
  title: { type: 'string' },
  aspectRatio: { type: ['number', 'string'] },
  maxSize: { type: 'number' },
  minDimensions: {
    type: 'object',
    shape: { width: { type: 'number' }, height: { type: 'number' } },
  },
  required: { type: 'boolean' },
  minImages: { type: 'number' },
  maxImages: { type: 'number' },
};

/**
 * ImageUploader
 *
 * Modal-based image upload component supporting single and multiple images.
 * Handles client-side validation (file size, dimensions, aspect ratio),
 * preview rendering, and normalized output.
 *
 * This component manages object URL lifecycles for files it creates.
 *
 * @component
 *
 * @param {Object} props
 *
 * @param {boolean} props.open
 * Controls whether the uploader modal is open.
 *
 * @param {Function} props.onClose
 * Callback invoked when the modal is closed.
 *
 * @param {boolean} [props.multiple=false]
 * Enables multiple image selection and preview grid.
 *
 * @param {Array<{ url: string, file?: File }>} [props.initialValue]
 * Initial images to populate the uploader.
 * If `file` is omitted, the URL lifecycle is assumed to be managed externally.
 *
 * @param {Function} [props.onSave]
 * Called when the user clicks Save.
 * Receives an array of normalized image items:
 * `{ url: string, file?: File }[]`.
 *
 * @param {string} [props.title]
 * Title displayed at the top of the uploader modal.
 *
 * @param {number|string} [props.aspectRatio]
 * Image aspect ratio constraint.
 * - `number`: treated as width / height
 * - `"w:h"` string: parsed as width:height
 *
 * @param {number} [props.maxSize]
 * Maximum allowed file size in **megabytes (MB)**.
 *
 * @param {{ width: number, height: number }} [props.minDimensions]
 * Minimum required image dimensions in pixels.
 *
 * @param {boolean} [props.required=false]
 * Whether at least one image is required.
 * When `true`, saving is blocked until the minimum image requirement is met.
 *
 * @param {number} [props.minImages]
 * Minimum number of images required.
 * If `required` is `true` and this is not provided, a minimum of 1 image is enforced.
 *
 * @param {number} [props.maxImages]
 * Maximum number of images allowed.
 *
 * @returns {JSX.Element}
 */
export default function ImageUploader(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'ImageUploader');
  const {
    open,
    onClose,
    multiple,
    initialValue,
    aspectRatio,
    maxSize,
    minDimensions,
    minImages,
    maxImages,
    required = false,
  } = props;

  const effectiveMinImages = resolveMinImages({ required, minImages });

  const [items, setItems] = useState(initialValue || []);
  const [removedItems, setRemovedItems] = useState([]);
  const count = items.length;

  const isBelowMin = typeof effectiveMinImages === 'number' && count < effectiveMinImages;
  const isAboveMax = typeof maxImages === 'number' && count > maxImages;

  const [errors, setErrors] = useState([]);

  const { aspectRatioValue, aspectRatioLabel } = useMemo(
    () => getAspectRatio(aspectRatio),
    [aspectRatio],
  );

  const hasErrors = errors.some((e) => !e.isValid) || isBelowMin || isAboveMax;
  const errorMessages = useMemo(
    () =>
      generateUploaderErrors(errors, {
        maxSize,
        minDimensions,
        aspectRatioLabel,
        minImages: effectiveMinImages,
        maxImages,
        imageCount: count,
      }),
    [errors, maxSize, minDimensions, aspectRatioLabel, effectiveMinImages, maxImages, count],
  );

  const hintMessage = useMemo(
    () => generateUploaderHints({ maxSize, minDimensions, aspectRatioLabel }),
    [maxSize, minDimensions, aspectRatioLabel],
  );

  useEffect(() => {
    if (!open) return;

    const normalized = (initialValue || [])
      .map((item) => {
        if (item.url) return item;

        if (item.file instanceof File) {
          return {
            file: item.file,
            url: URL.createObjectURL(item.file as Blob),
          };
        }

        return null;
      })
      .filter(Boolean);

    setItems(normalized);
    setErrors(normalized.map(() => ({ isValid: true })));
  }, [open, initialValue]);

  const previews = useMemo(() => items.map((i) => i.url), [items]);

  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const validations = await Promise.all(
      selected.map((file) =>
        validateFileToUpload(file, {
          maxSize,
          minDimensions,
          aspectRatioValue,
        }),
      ),
    );

    const newItems = selected.map((file) => ({
      file,
      url: URL.createObjectURL(file as Blob),
    }));

    setItems((prev) => (multiple ? [...prev, ...newItems] : [newItems[0]]));
    setErrors((prev) => (multiple ? [...prev, ...validations] : [validations[0]]));

    e.target.value = '';
  };

  const handleRemove = (index) => {
    const removedItem = items[index];
    setRemovedItems((prev) => [...prev, removedItem]);
    setItems((prev) => prev.filter((_, i) => i !== index));

    setErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const itemsToSave = items.filter((item) => !removedItems.includes(item));
    removedItems.forEach((ri) => {
      if (ri?.file && ri.url.startsWith('blob:')) {
        URL.revokeObjectURL(ri.url);
      }
    });
    props.onSave?.(itemsToSave);
    onClose();
  };

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      maxWidth={multiple ? (previews.length > 1 ? 'lg' : previews.length > 0 ? 'md' : 'sm') : 'sm'}
    >
      <SectionTitle title={props.title || 'Upload Image'} />

      <Stack gap={2} padding={3} alignItems='center' width='100%'>
        {multiple ? (
          <MultiImageGrid
            previews={previews}
            maxImages={maxImages}
            aspectRatioValue={aspectRatioValue}
            images={items}
            onChange={handleFiles}
            errors={errors}
            onDelete={handleRemove}
          />
        ) : (
          <SingleImagePreview
            images={items}
            aspectRatioValue={aspectRatioValue}
            onChange={handleFiles}
            previews={previews}
            errors={errors}
            onDelete={handleRemove}
          />
        )}

        <UploaderError hasErrors={hasErrors} errorMessages={errorMessages} />
        <UploaderHints hintMessage={hintMessage} />
      </Stack>

      <Stack direction='row' gap={2} justifyContent='flex-end' width='100%' sx={{ p: 3, pt: 1 }}>
        <button type='button' className='btn outlined-btn' onClick={onClose}>
          Cancel
        </button>
        <button type='button' className='btn cta-btn' onClick={handleSave} disabled={hasErrors}>
          Save
        </button>
      </Stack>
    </ModalWrapper>
  );
}

function UploaderError({ hasErrors, errorMessages }) {
  if (!hasErrors) return null;
  return <Alert severity='error'>{errorMessages}</Alert>;
}

function UploaderHints({ hintMessage }) {
  if (!hintMessage.length) return null;
  return (
    <Typography variant='caption' color='grey.500' sx={{ textAlign: 'center', mt: 1 }}>
      {hintMessage}
    </Typography>
  );
}
